import type { Identifier, RaRecord } from "ra-core";
import {
  FINANCIAL_ZONES,
  type FinancePlan,
  type ZoneKey,
} from "./financeModel";
import type { ExpenseTotal } from "./expenseModel";
export const BUDGET_ZONES = [
  ...FINANCIAL_ZONES,
  { key: "reserve", label: "Грязная прибыль проекта", percent: 0 },
] as const;
export type BudgetZone = ZoneKey | "reserve";
export const ADJUSTMENT_REASONS = [
  { value: "reallocation", label: "Перераспределение бюджета" },
  { value: "employee_error", label: "Ошибка сотрудника" },
  { value: "production_error", label: "Ошибка производства" },
  { value: "client_change", label: "Изменение клиента" },
  { value: "force_majeure", label: "Форс-мажор" },
  { value: "bonus", label: "Дополнительное вознаграждение" },
] as const;
export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number]["value"];
export interface BudgetAdjustment extends RaRecord {
  deal_id: Identifier;
  revision: number;
  plan_id: Identifier;
  from_zone: BudgetZone;
  to_zone: BudgetZone;
  amount: number;
  reason: AdjustmentReason;
  note: string;
  sales_id: Identifier | null;
  company_id: Identifier | null;
  reverses_id: Identifier | null;
  created_by_sales_id: Identifier;
  created_at: string;
}
export type AdjustmentInput = Pick<
  BudgetAdjustment,
  | "deal_id"
  | "from_zone"
  | "to_zone"
  | "amount"
  | "reason"
  | "note"
  | "sales_id"
  | "company_id"
  | "reverses_id"
> & { expected_plan_revision: number; expected_revision: number };
export function validateAdjustment(input: AdjustmentInput) {
  if (
    input.from_zone === input.to_zone ||
    !BUDGET_ZONES.some((z) => z.key === input.from_zone) ||
    !BUDGET_ZONES.some((z) => z.key === input.to_zone)
  )
    throw new Error("Выберите две разные зоны");
  if (
    !Number.isFinite(input.amount) ||
    input.amount <= 0 ||
    input.amount > 9999999999.99 ||
    Math.abs(input.amount * 100 - Math.round(input.amount * 100)) > 0.0001
  )
    throw new Error(
      "Укажите положительную сумму, до двух знаков после запятой",
    );
  if (!input.note.trim() || input.note.trim().length > 2000)
    throw new Error("Укажите основание корректировки");
  if (!ADJUSTMENT_REASONS.some((r) => r.value === input.reason))
    throw new Error("Выберите причину");
  if (input.sales_id != null && input.company_id != null)
    throw new Error("Выберите одного ответственного");
}
export function adjustedBalances(
  plan: FinancePlan,
  totals: ExpenseTotal[],
  adjustments: BudgetAdjustment[],
) {
  const relevant = adjustments.filter(
    (a) => String(a.deal_id) === String(plan.deal_id),
  );
  const zoneBudget = plan.zones.reduce(
    (sum, z) => sum + Math.round(Number(z.budget) * 100),
    0,
  );
  return BUDGET_ZONES.map((zone) => {
    const base =
      zone.key === "reserve"
        ? Math.round(Number(plan.price) * 100) - zoneBudget
        : Math.round(
            Number(plan.zones.find((z) => z.key === zone.key)?.budget ?? 0) *
              100,
          );
    const change = relevant.reduce(
      (sum, a) =>
        sum +
        (a.to_zone === zone.key ? Math.round(Number(a.amount) * 100) : 0) -
        (a.from_zone === zone.key ? Math.round(Number(a.amount) * 100) : 0),
      0,
    );
    const spent = totals
      .filter(
        (t) =>
          String(t.deal_id) === String(plan.deal_id) && t.zone_key === zone.key,
      )
      .reduce((sum, t) => sum + Math.round(Number(t.approved_amount) * 100), 0);
    return {
      key: zone.key,
      label: zone.label,
      base: base / 100,
      change: change / 100,
      budget: (base + change) / 100,
      spent: spent / 100,
      balance: (base + change - spent) / 100,
    };
  });
}
